// The enrolment QR, drawn IN THE BROWSER as inline SVG (spec 15 §3.1.3).
//
// The payload is an `otpauth://` URI carrying the TOTP secret, so it may not
// travel to an image service, a CDN, or any third party — it is encoded by
// `lib/qr.ts` (bit-exact against an independent implementation, see its test)
// and rendered as one <path> of module rectangles. No canvas, no data: URI, no
// network. Black modules on white are a REQUIREMENT of the format, not a
// styling choice, so the two colors come from the token layer's ink/surface
// roles and the frame follows the register (1 px border, radius 8).
//
// If the payload is somehow larger than the encoder's ceiling the component
// renders NOTHING and tells the caller, which is what puts the manual-entry
// path on screen instead of an unreadable square (D-033: no fake success).

import { useMemo } from "react";
import { encodeQr } from "../lib/qr";

/** Quiet zone required by the spec: four modules of blank on every side. */
const QUIET_ZONE = 4;

export interface QrCodeProps {
  /** The text to encode — here, always the otpauth URI. */
  value: string;
  /** Accessible name; the code itself is not readable by assistive tech. */
  title: string;
  /** Rendered edge in CSS pixels. */
  size?: number;
  "data-testid"?: string;
  /** Rendered instead of the code when the value cannot be encoded. */
  fallback: React.ReactNode;
}

export function QrCode({ value, title, size = 176, fallback, ...rest }: QrCodeProps) {
  // Encoding is pure and deterministic; memo keeps a re-render (a keystroke in
  // the code field below it) from re-running the Reed-Solomon pass. `value` is
  // the ONLY input the matrix depends on.
  const matrix = useMemo(() => {
    try {
      return encodeQr(value);
    } catch {
      return undefined;
    }
  }, [value]);

  if (matrix === undefined) return <>{fallback}</>;

  const span = matrix.size + QUIET_ZONE * 2;
  // One path, one fill: every dark module as a 1×1 rect in the path data. This
  // is both the smallest DOM (a v7 symbol is ~700 modules) and the crispest
  // rendering — integer coordinates in a viewBox that is exactly the module
  // count, so no module ever lands on a half pixel.
  let d = "";
  for (let row = 0; row < matrix.size; row++) {
    for (let col = 0; col < matrix.size; col++) {
      if (matrix.dark[row * matrix.size + col] === true) {
        d += `M${String(col + QUIET_ZONE)} ${String(row + QUIET_ZONE)}h1v1h-1z`;
      }
    }
  }

  return (
    <svg
      className="admin-qr"
      viewBox={`0 0 ${String(span)} ${String(span)}`}
      width={size}
      height={size}
      role="img"
      aria-label={title}
      shapeRendering="crispEdges"
      {...rest}
    >
      <rect width={span} height={span} className="admin-qr-bg" />
      <path d={d} className="admin-qr-fg" />
    </svg>
  );
}
